/**
 * whiteboardProjection — pure layout for the D2P "projection view".
 *
 * Turns a board baseline into projector-ready geometry: relationship edges as
 * line endpoints in a 0–100 coordinate space (percent of the projection
 * surface), with danglers dropped. The view component renders boxes from the
 * baseline items directly; this module exists so the edge math is testable.
 */

import type { BoardBaseline } from './whiteboardMerge';
import type { RelationshipEdgeType } from '../types/prioritization/itemRelationships';

/**
 * The registration frame. Board coordinates (0,0)→(1,1) map into a rectangle
 * inset from the projection surface by this fraction on every side, and the four
 * corner registration marks' L-vertices sit at the inset corners — i.e. exactly
 * at board (0,0),(1,0),(1,1),(0,1). Projected content and the fiducial marks
 * therefore share one frame, so a re-capture's homography (marks → unit square)
 * lands recognized positions in the identical frame the boxes were drawn in. The
 * inset keeps the marks clear of projector overscan / edge clipping.
 */
export const REGISTRATION_MARGIN = 0.025;
const SPAN = 1 - 2 * REGISTRATION_MARGIN;

/** Board coordinate (0–1) → surface percentage (0–100), through the frame. */
export function boardToSurfacePct(v: number): number {
  return (REGISTRATION_MARGIN + v * SPAN) * 100;
}

/** Inverse: surface fraction (0–1) → board coordinate, clamped to [0,1]. */
export function surfaceToBoard(frac: number): number {
  return Math.min(1, Math.max(0, (frac - REGISTRATION_MARGIN) / SPAN));
}

export interface ProjectionEdge {
  /** Endpoints in 0–100 space (percent of the surface). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  directed: boolean;
  type: RelationshipEdgeType;
}

/**
 * Build the edges to draw between projected boxes. Relationships whose endpoints
 * aren't in the baseline's items are dropped. Pure & deterministic.
 */
export function buildProjectionEdges(baseline: BoardBaseline): ProjectionEdge[] {
  const pos = new Map(baseline.items.map((i) => [i.id, i.position] as const));
  const edges: ProjectionEdge[] = [];
  for (const r of baseline.relationships) {
    const s = pos.get(r.sourceItemId);
    const t = pos.get(r.targetItemId);
    if (!s || !t) continue;
    edges.push({
      x1: s.x * 100,
      y1: s.y * 100,
      x2: t.x * 100,
      y2: t.y * 100,
      directed: r.directionality === 'directed',
      type: r.type,
    });
  }
  return edges;
}
